/**
 * Штрафы.
 *
 * Слева правила, справа объяснение логики — чтобы было видно, почему
 * штраф пришёл именно этому человеку. Ниже журнал начислений с кнопкой
 * отмены: автоматический штраф должен оставаться решением, которое
 * можно обсудить, иначе он разрушает команду вместо дисциплины.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Empty, Loading, Toggle, toast } from '../components/ui';
import { dateTime, money } from '../lib/format';

type Rule = {
  id: string;
  name: string;
  trigger: 'OVERDUE_SHOOT' | 'OVERDUE_EDIT';
  thresholdHours: number;
  amount: number;
  isActive: boolean;
};

type Applied = {
  id: string;
  userId: string;
  amount: number;
  overdueHours: number;
  appliedAt: string;
  cancelledAt: string | null;
  trigger: string;
  video: { id: string; index: number; task: { number: number; title: string } };
};

export function PenaltiesPage() {
  const qc = useQueryClient();

  const { data: rules, isLoading } = useQuery({
    queryKey: ['penalty-rules'],
    queryFn: () => api.get<Rule[]>('/api/finance/penalty-rules'),
  });

  const { data: applied } = useQuery({
    queryKey: ['penalties'],
    queryFn: () => api.get<Applied[]>('/api/finance/penalties'),
  });

  const save = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) =>
      api.patch(`/api/finance/penalty-rules/${id}`, { amount }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['penalty-rules'] });
      toast('Сохранено');
    },
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/api/finance/penalties/${id}/cancel`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['penalties'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
      toast('Штраф отменён, деньги возвращены');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const auto = useMutation({
    mutationFn: (enabled: boolean) => api.post('/api/finance/penalty-rules/auto', { enabled }),
    onSuccess: () => toast('Настройка сохранена'),
  });

  // Правила одинаковы для съёмки и монтажа — показываем один список
  const shootRules = rules?.filter((r) => r.trigger === 'OVERDUE_SHOOT') ?? [];

  return (
    <>
      <PageHeader title="Штрафы" sub="Правила автоначисления" />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            <div className="sec !mt-0">Просрочка этапа</div>
            {isLoading ? (
              <Loading rows={4} />
            ) : (
              <div className="card">
                {shootRules.map((r) => (
                  <div
                    key={r.id}
                    className="flex justify-between items-center py-3 border-b border-[#1A1A24] last:border-0 first:pt-0 last:pb-0"
                  >
                    <span className="font-semibold">{thresholdLabel(r.thresholdHours)}</span>
                    <div className="flex items-center gap-2">
                      <input
                        className="input mono w-28 text-right py-1.5"
                        defaultValue={r.amount / 100}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isNaN(v) && v * 100 !== r.amount) {
                            // Правило применяется к обоим этапам сразу
                            const twin = rules?.find(
                              (x) =>
                                x.thresholdHours === r.thresholdHours &&
                                x.trigger === 'OVERDUE_EDIT',
                            );
                            save.mutate({ id: r.id, amount: v });
                            if (twin) save.mutate({ id: twin.id, amount: v });
                          }
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                      />
                      <span className="text-muted2 text-xs w-3">₽</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="card mt-3 flex justify-between items-center">
              <div>
                <div className="font-semibold">Автоматическое начисление</div>
                <div className="text-[11.5px] text-muted2 mt-0.5">
                  Проверка просрочек каждые 10 минут
                </div>
              </div>
              <Toggle on onChange={(v) => auto.mutate(v)} />
            </div>

            <div className="sec">Начисленные штрафы</div>
            {!applied?.length ? (
              <div className="card">
                <Empty title="Штрафов не было" hint="Команда укладывается в сроки" />
              </div>
            ) : (
              <div className="card p-0 overflow-hidden">
                {applied.map((p) => (
                  <div
                    key={p.id}
                    className={`flex items-center gap-4 px-4 py-3.5 border-b border-[#1A1A24] last:border-0 ${
                      p.cancelledAt ? 'opacity-45' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">
                        {p.video.task.title} · видео {p.video.index}
                      </div>
                      <div className="text-[11.5px] text-muted2">
                        {p.trigger === 'OVERDUE_SHOOT' ? 'съёмка' : 'монтаж'} · опоздание{' '}
                        {p.overdueHours} ч · {dateTime(p.appliedAt)}
                      </div>
                    </div>
                    <span className="mono font-bold text-dang">{money(-p.amount)}</span>
                    {p.cancelledAt ? (
                      <span className="text-[11px] text-muted2 w-20 text-right">отменён</span>
                    ) : (
                      <button
                        className="btn btn-sm w-20"
                        onClick={() => cancel.mutate(p.id)}
                        disabled={cancel.isPending}
                      >
                        Отменить
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="sec !mt-0">Как это работает</div>
            <div className="card text-[13px] space-y-3.5">
              <Note
                first
                text="Виноват владелец этапа"
                sub="съёмка просрочена — воркер, монтаж — монтажер"
              />
              <Note
                text="Один штраф на этап"
                sub="повторно за то же самое не начислится"
              />
              <Note
                text="На проверке штрафа нет"
                sub="пока видео ждёт тебя, исполнитель не виноват"
              />
              <Note
                text="Простой сервера не считается"
                sub="если система лежала, это время вычитается из просрочки"
              />
              <Note
                text="Отмена возвращает деньги"
                sub="встречной записью, исходная остаётся в истории"
              />
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

function Note({ text, sub, first }: { text: string; sub: string; first?: boolean }) {
  return (
    <div className="flex gap-3">
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${first ? 'bg-accent' : 'bg-[#31314A]'}`} />
      <div>
        <div>{text}</div>
        <div className="text-[11.5px] text-muted2 mt-0.5 leading-snug">{sub}</div>
      </div>
    </div>
  );
}

const thresholdLabel = (hours: number) =>
  hours >= 1000 ? 'Более 24 часов' : `До ${hours} ${hours === 1 ? 'часа' : 'часов'}`;
